import '@blocknote/core/style.css';
import '@blocknote/react/style.css';
import '@blocknote/ariakit/style.css';
import './block-editor.web.css';

import type { PartialBlock } from '@blocknote/core';
import { BlockNoteView } from '@blocknote/ariakit';
import type { SyncDelta } from '@couplespace/sync';
import {
  AddBlockButton,
  DragHandleButton,
  SideMenu,
  SideMenuController,
  useCreateBlockNote,
} from '@blocknote/react';
import { Component, type ErrorInfo, type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { ActivityIndicator, Pressable, View } from 'react-native';
import { useRouter } from 'expo-router';

import { Text } from '@/components/ui';

import { fetchPageDocument, presignDownload, presignUpload, updateBlock } from '@/lib/blocks/api';
import { invalidatePageDocument } from '@/lib/blocks/page-cache';
import type { BlockRecord } from '@/lib/blocks/types';
import { useWebSyncPage } from '@/lib/sync/web-sync-context';

import { blocksToPartialBlocks, flattenPartialBlocks } from './blocknote-types';
import {
  createCouplespaceMarkdownCopyExtension,
  createCouplespacePasteHandler,
} from './couplespace-markdown-clipboard.web';
import { CouplespaceDragHandleMenu } from './CouplespaceDragHandleMenu.web';
import { CouplespaceFormattingToolbarController } from './CouplespaceFormattingToolbar.web';
import { CouplespaceSlashMenuController } from './CouplespaceSlashMenu.web';
import { couplespaceTableEnterExtension } from './couplespace-table-enter.web';
import { couplespaceSchema } from './editor-schema.web';
import { DatabaseViewPickerHost } from './DatabaseViewPickerHost.web';
import { EditorDatabaseViewPickerProvider } from './editor-database-view-picker-context.web';
import { EditorLinkPickerProvider } from './editor-link-picker-context.web';
import { EditorPageProvider } from './editor-page-context.web';
import {
  blocksContentEqual,
  fixInlineLinkArtifacts,
  repairPartialBlocks,
  validateBlocksForPersist,
} from './sanitize-initial-content';
import { FILE_BLOCK_ID_PROP } from './file-block-persist';
import {
  applyRemotePatchToSnapshot,
  planRemotePatch,
  serverPositionsAfterSave,
} from './remote-patch';
import { isRetryableSaveError, SAVE_RETRY_MESSAGE, saveRetryDelay } from './save-retry';
import { buildSnapshot, syncDocument, type SyncSnapshot } from './sync-document.web';

type UploadFileResult = string | { props: Record<string, unknown> };

function isConflictError(err: unknown): boolean {
  if (!(err instanceof Error)) {
    return false;
  }
  return (
    err.message.includes('CONFLICT') || err.message.includes('modified by another session')
  );
}

async function rebaseSnapshotFromServer(
  pageId: string,
  keepIds: ReadonlySet<string>,
): Promise<{ snapshot: SyncSnapshot; serverPositions: Map<string, number> }> {
  invalidatePageDocument(pageId);
  const doc = await fetchPageDocument(pageId);
  const versions = new Map<string, number>();
  const serverPositions = new Map<string, number>();
  for (const row of doc.contentBlocks) {
    versions.set(row.id, row.syncVersion);
    serverPositions.set(row.id, row.position);
  }
  const serverBlocks = blocksToPartialBlocks(doc.block.id, doc.contentBlocks);
  const snapshot = buildSnapshot(pageId, serverBlocks, versions);
  // Blocks another session added that this editor never saw must stay out of
  // the snapshot, or the next save would delete them.
  return {
    snapshot: { ...snapshot, flat: snapshot.flat.filter((block) => keepIds.has(block.id)) },
    serverPositions,
  };
}

function collectImageBlockSummary(blocks: PartialBlock[]): string {
  const out: Array<{
    id: string | undefined;
    type: string;
    props: Record<string, unknown>;
    content: unknown;
  }> = [];
  function visit(block: PartialBlock): void {
    if (block.type === 'image' || block.type === 'file') {
      out.push({
        id: block.id,
        type: block.type,
        props: { ...((block.props as Record<string, unknown> | undefined) ?? {}) },
        content: block.content,
      });
    }
    block.children?.forEach(visit);
  }
  blocks.forEach(visit);
  return JSON.stringify(out);
}

function uploadFilename(file: File): string {
  const trimmed = file.name.trim();
  if (trimmed.length > 0) {
    return trimmed;
  }
  const subtype = file.type.split('/')[1];
  if (subtype) {
    return subtype === 'jpeg' ? 'upload.jpg' : `upload.${subtype}`;
  }
  return 'upload.bin';
}

type BlockEditorProps = {
  pageId: string;
  pageTitle: string;
  pageSyncVersion: number;
  onPageTitleChange?: (title: string) => void;
  initialContent: PartialBlock[];
  syncVersions: Map<string, number>;
  serverPositions?: Map<string, number>;
  loading?: boolean;
  onRemoteReload?: () => void;
  onRemotePageBlock?: (block: BlockRecord) => void;
};

export function BlockEditor({
  pageId,
  pageTitle,
  pageSyncVersion,
  onPageTitleChange,
  initialContent,
  syncVersions,
  serverPositions,
  loading,
  onRemoteReload,
  onRemotePageBlock,
}: BlockEditorProps) {
  if (loading) {
    return (
      <View className="min-h-full flex-1 items-center justify-center">
        <ActivityIndicator size="small" />
      </View>
    );
  }

  return (
    <BlockEditorErrorBoundary>
      <BlockNoteEditorInner
        key={pageId}
        initialContent={initialContent}
        onPageTitleChange={onPageTitleChange}
        onRemotePageBlock={onRemotePageBlock}
        onRemoteReload={onRemoteReload}
        pageId={pageId}
        pageSyncVersion={pageSyncVersion}
        pageTitle={pageTitle}
        serverPositions={serverPositions}
        syncVersions={syncVersions}
      />
    </BlockEditorErrorBoundary>
  );
}

type BlockEditorErrorBoundaryProps = {
  children: ReactNode;
};

type BlockEditorErrorBoundaryState = {
  error: Error | null;
};

class BlockEditorErrorBoundary extends Component<
  BlockEditorErrorBoundaryProps,
  BlockEditorErrorBoundaryState
> {
  state: BlockEditorErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): BlockEditorErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('Block editor failed to render', error, info.componentStack);
  }

  render(): ReactNode {
    if (this.state.error) {
      return (
        <View className="mx-auto my-6 w-full max-w-content rounded-lg border border-danger bg-danger-muted px-4 py-3">
          <Text variant="body-sm" className="font-medium text-danger">
            Editor failed to load
          </Text>
          <Text variant="body-sm" className="mt-1 text-fg-muted">
            This page has content the editor could not recover. Reload the page or reset the
            document below.
          </Text>
          <Pressable
            className="mt-3 self-start rounded-md bg-danger px-3 py-2"
            onPress={() => this.setState({ error: null })}
          >
            <Text variant="body-sm" className="text-fg-inverse">
              Try again
            </Text>
          </Pressable>
        </View>
      );
    }
    return this.props.children;
  }
}

type BlockNoteEditorInnerProps = {
  pageId: string;
  pageTitle: string;
  pageSyncVersion: number;
  onPageTitleChange?: (title: string) => void;
  initialContent: PartialBlock[];
  syncVersions: Map<string, number>;
  serverPositions?: Map<string, number>;
  onRemoteReload?: () => void;
  onRemotePageBlock?: (block: BlockRecord) => void;
};

function BlockNoteEditorInner({
  pageId,
  pageTitle,
  pageSyncVersion,
  onPageTitleChange,
  initialContent,
  syncVersions,
  serverPositions,
  onRemoteReload,
  onRemotePageBlock,
}: BlockNoteEditorInnerProps) {
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const { registerEditor, notifyLocalSave } = useWebSyncPage(pageId);
  const { blocks: safeInitialContent, repaired: contentWasRepaired } = useMemo(
    () => repairPartialBlocks(initialContent),
    [initialContent],
  );
  const snapshotRef = useRef<SyncSnapshot>(
    buildSnapshot(pageId, safeInitialContent, syncVersions),
  );
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingRef = useRef(false);
  const savingRef = useRef(false);
  const saveQueuedRef = useRef(false);
  const serverPositionsRef = useRef(new Map<string, number>(serverPositions));
  const remoteQueueRef = useRef<SyncDelta[]>([]);
  const deferredRemoteRef = useRef(new Map<string, SyncDelta>());
  const applyingRemoteRef = useRef(false);
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const retryAttemptRef = useRef(0);
  const rebaseBeforeSaveRef = useRef(false);
  const onRemoteReloadRef = useRef(onRemoteReload);
  const onRemotePageBlockRef = useRef(onRemotePageBlock);
  onRemoteReloadRef.current = onRemoteReload;
  onRemotePageBlockRef.current = onRemotePageBlock;
  const repairPersistedRef = useRef(false);
  const uploadsInFlightRef = useRef(0);
  const repairingLinksRef = useRef(false);
  const editorShellRef = useRef<HTMLDivElement | null>(null);
  const router = useRouter();
  const flushSaveRef = useRef<(forceBlocks?: PartialBlock[]) => Promise<void>>(() =>
    Promise.resolve(),
  );
  const pageTitleRef = useRef(pageTitle);
  const pageSyncVersionRef = useRef(pageSyncVersion);
  const editorRef = useRef<ReturnType<typeof useCreateBlockNote<{
    schema: typeof couplespaceSchema;
  }>> | null>(null);
  pageTitleRef.current = pageTitle;
  pageSyncVersionRef.current = pageSyncVersion;

  const markdownClipboardRefs = useMemo(
    () => ({
      getPageTitle: () => pageTitleRef.current,
      onPageTitleFromPaste: async (title: string) => {
        const trimmed = title.trim() || 'Untitled';
        const updated = await updateBlock(pageId, {
          title: trimmed,
          expectedSyncVersion: pageSyncVersionRef.current,
        });
        pageSyncVersionRef.current = updated.syncVersion;
        onPageTitleChange?.(trimmed);
      },
    }),
    [onPageTitleChange, pageId],
  );

  const markdownCopyExtension = useMemo(
    () =>
      createCouplespaceMarkdownCopyExtension(
        () => editorRef.current,
        markdownClipboardRefs,
      ),
    [markdownClipboardRefs],
  );

  const uploadFile = useMemo(
    () => async (file: File): Promise<UploadFileResult> => {
      uploadsInFlightRef.current += 1;
      notifyLocalSave();
      console.log('[CS-IMG] uploadFile:start', {
        name: file.name,
        type: file.type,
        size: file.size,
        inFlight: uploadsInFlightRef.current,
      });
      try {
        const { fileId, uploadUrl } = await presignUpload({
          blockId: pageId,
          filename: uploadFilename(file),
          mimeType: file.type || 'application/octet-stream',
          sizeBytes: file.size,
        });
        console.log('[CS-IMG] presignUpload:ok', { fileId, uploadUrl });
        const putRes = await fetch(uploadUrl, {
          method: 'PUT',
          body: file,
          headers: { 'Content-Type': file.type || 'application/octet-stream' },
        });
        console.log('[CS-IMG] put:done', { status: putRes.status, ok: putRes.ok });
        if (!putRes.ok) {
          throw new Error(`Upload failed (${putRes.status})`);
        }
        const url = await presignDownload(fileId);
        console.log('[CS-IMG] presignDownload:ok', { fileId, url });
        const result = {
          props: {
            [FILE_BLOCK_ID_PROP]: fileId,
            url,
            name: uploadFilename(file),
          },
        };
        console.log('[CS-IMG] uploadFile:return', result);
        return result;
      } catch (err) {
        console.error('[CS-IMG] uploadFile:error', err);
        throw err;
      } finally {
        uploadsInFlightRef.current -= 1;
        console.log('[CS-IMG] uploadFile:finally', {
          inFlight: uploadsInFlightRef.current,
        });
        globalThis.setTimeout(() => {
          console.log('[CS-IMG] post-upload flushSave trigger');
          void flushSaveRef.current();
        }, 0);
      }
    },
    [notifyLocalSave, pageId],
  );

  const editor = useCreateBlockNote({
    schema: couplespaceSchema,
    extensions: [couplespaceTableEnterExtension, markdownCopyExtension],
    initialContent: safeInitialContent.length > 0 ? safeInitialContent : undefined,
    pasteHandler: createCouplespacePasteHandler(markdownClipboardRefs),
    uploadFile,
  });
  editorRef.current = editor;

  // Compare remote rows against the editor's normalised form (default props
  // filled in), or blocks never saved from an editor would all look locally edited.
  const snapshotNormalizedRef = useRef(false);
  if (!snapshotNormalizedRef.current) {
    snapshotNormalizedRef.current = true;
    snapshotRef.current = buildSnapshot(pageId, editor.document as PartialBlock[], syncVersions);
  }

  function hasUnsavedChanges(): boolean {
    return (
      pendingRef.current ||
      savingRef.current ||
      debounceRef.current !== null ||
      retryTimerRef.current !== null ||
      uploadsInFlightRef.current > 0
    );
  }

  function focusedBlockId(): string | null {
    try {
      return editor.isFocused() ? editor.getTextCursorPosition().block.id : null;
    } catch {
      return null;
    }
  }

  function applyRemoteDeltas(incoming: SyncDelta[]): boolean {
    if (pendingRef.current || savingRef.current) {
      remoteQueueRef.current.push(...incoming);
      return true;
    }
    const deltas = [...deferredRemoteRef.current.values(), ...incoming];
    deferredRemoteRef.current = new Map();
    const snapshot = snapshotRef.current;
    const localFlat = flattenPartialBlocks(
      pageId,
      editor.document as PartialBlock[],
      snapshot.syncVersions,
    );
    const plan = planRemotePatch({
      pageId,
      snapshot,
      localFlat,
      serverPositions: serverPositionsRef.current,
      deltas,
      focusedBlockId: focusedBlockId(),
    });
    if (plan.pageRow) {
      onRemotePageBlockRef.current?.(plan.pageRow);
    }
    if (plan.needsReload) {
      // Kept for a later retry in case the reload is skipped (unsaved edits).
      for (const delta of deltas) {
        deferredRemoteRef.current.set(delta.rowId, delta);
      }
      return false;
    }
    for (const delta of plan.deferred) {
      deferredRemoteRef.current.set(delta.rowId, delta);
    }
    for (const [id, position] of plan.serverPositions) {
      serverPositionsRef.current.set(id, position);
    }
    if (plan.ops.length === 0 && plan.versions.size === 0) {
      return true;
    }

    applyingRemoteRef.current = true;
    try {
      editor.transact((tr) => {
        tr.setMeta('addToHistory', false);
        for (const op of plan.ops) {
          if (op.kind === 'remove') {
            editor.removeBlocks(op.ids);
          } else if (op.kind === 'update') {
            editor.updateBlock(op.id, op.block as Parameters<typeof editor.updateBlock>[1]);
          } else {
            editor.insertBlocks(
              [op.block] as Parameters<typeof editor.insertBlocks>[0],
              op.reference.id,
              op.reference.placement,
            );
          }
        }
      });
    } catch (err) {
      console.error('[CS-LIVE] remote patch failed', err);
      return false;
    } finally {
      applyingRemoteRef.current = false;
    }

    snapshotRef.current = applyRemotePatchToSnapshot({
      snapshot,
      plan,
      localFlatBefore: localFlat,
      editorFlatAfter: flattenPartialBlocks(
        pageId,
        editor.document as PartialBlock[],
        snapshot.syncVersions,
      ),
    });
    return true;
  }

  function drainRemoteQueue(): void {
    if (remoteQueueRef.current.length === 0 && deferredRemoteRef.current.size === 0) {
      return;
    }
    const queued = remoteQueueRef.current.splice(0);
    if (applyRemoteDeltas(queued)) {
      return;
    }
    if (hasUnsavedChanges()) {
      remoteQueueRef.current.unshift(...queued);
      return;
    }
    onRemoteReloadRef.current?.();
  }

  const applyRemoteRef = useRef(applyRemoteDeltas);
  applyRemoteRef.current = applyRemoteDeltas;
  const focusedBlockIdRef = useRef(focusedBlockId);
  focusedBlockIdRef.current = focusedBlockId;
  const hasUnsavedChangesRef = useRef(hasUnsavedChanges);
  hasUnsavedChangesRef.current = hasUnsavedChanges;

  const drainRemoteQueueRef = useRef(drainRemoteQueue);
  drainRemoteQueueRef.current = drainRemoteQueue;

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null;
    const unsubscribe = editor.onSelectionChange(() => {
      if (timer || deferredRemoteRef.current.size === 0) {
        return;
      }
      timer = setTimeout(() => {
        timer = null;
        if (debounceRef.current !== null) {
          return;
        }
        const focused = focusedBlockIdRef.current();
        if (focused && deferredRemoteRef.current.has(focused)) {
          return;
        }
        drainRemoteQueueRef.current();
      }, 0);
    });
    return () => {
      unsubscribe();
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [editor]);

  useEffect(() => {
    if (!onRemoteReload) {
      return;
    }
    return registerEditor({
      isDirty: () => hasUnsavedChangesRef.current(),
      reload: onRemoteReload,
      blockIds: () => {
        const ids = new Set<string>([pageId]);
        for (const block of snapshotRef.current.flat) {
          ids.add(block.id);
        }
        return ids;
      },
      applyRemote: (deltas) => applyRemoteRef.current(deltas),
    });
  }, [onRemoteReload, pageId, registerEditor]);

  function scheduleSaveRetry(): void {
    if (retryTimerRef.current) {
      return;
    }
    const delay = saveRetryDelay(retryAttemptRef.current);
    retryAttemptRef.current += 1;
    retryTimerRef.current = setTimeout(() => {
      retryTimerRef.current = null;
      void flushSaveRef.current();
    }, delay);
  }

  function clearSaveRetry(): void {
    if (retryTimerRef.current) {
      clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
    retryAttemptRef.current = 0;
  }

  async function rebaseSnapshot(blocks: PartialBlock[]): Promise<void> {
    const keepIds = new Set<string>(snapshotRef.current.flat.map((block) => block.id));
    for (const block of flattenPartialBlocks(pageId, blocks, snapshotRef.current.syncVersions)) {
      keepIds.add(block.id);
    }
    const rebased = await rebaseSnapshotFromServer(pageId, keepIds);
    snapshotRef.current = rebased.snapshot;
    serverPositionsRef.current = rebased.serverPositions;
  }

  async function persist(blocks: PartialBlock[]): Promise<void> {
    const previous = snapshotRef.current;
    snapshotRef.current = await syncDocument(pageId, blocks, previous);
    for (const [id, position] of serverPositionsAfterSave(previous, snapshotRef.current)) {
      serverPositionsRef.current.set(id, position);
    }
  }

  async function flushSave(forceBlocks?: PartialBlock[]): Promise<void> {
    if (forceBlocks === undefined && uploadsInFlightRef.current > 0) {
      console.log('[CS-IMG] flushSave:skip (upload in flight)', {
        inFlight: uploadsInFlightRef.current,
      });
      return;
    }
    if (pendingRef.current) {
      console.log('[CS-IMG] flushSave:queue (already saving)');
      saveQueuedRef.current = true;
      return;
    }
    const docSnapshot = forceBlocks ?? (editor.document as PartialBlock[]);
    console.log('[CS-IMG] flushSave:start', {
      forced: forceBlocks !== undefined,
      imageBlocks: collectImageBlockSummary(docSnapshot),
    });
    pendingRef.current = true;
    savingRef.current = true;
    setSaving(true);

    let blocksOverride = forceBlocks;
    let savedOk = false;
    let retryable = false;

    try {
      do {
        saveQueuedRef.current = false;
        setSaveError(null);

        const source = blocksOverride ?? (editor.document as PartialBlock[]);
        blocksOverride = undefined;

        const blocks = validateBlocksForPersist(source);
        if (!blocks) {
          console.warn('[CS-IMG] flushSave:invalid content, abort');
          setSaveError('Unable to save: document contains invalid content');
          return;
        }
        console.log('[CS-IMG] flushSave:persist', {
          imageBlocks: collectImageBlockSummary(blocks),
        });

        try {
          // A save that failed mid-way may have written part of the diff.
          if (rebaseBeforeSaveRef.current) {
            await rebaseSnapshot(blocks);
            rebaseBeforeSaveRef.current = false;
          }
          await persist(blocks);
          invalidatePageDocument(pageId);
          savedOk = true;
        } catch (err) {
          if (!isConflictError(err)) {
            throw err;
          }
          await rebaseSnapshot(blocks);
          await persist(blocks);
          invalidatePageDocument(pageId);
          savedOk = true;
        }
      } while (saveQueuedRef.current);
      console.log('[CS-IMG] flushSave:done', { savedOk });
    } catch (err) {
      console.error('[CS-IMG] flushSave:error', err);
      if (isRetryableSaveError(err)) {
        retryable = true;
        rebaseBeforeSaveRef.current = true;
        setSaveError(SAVE_RETRY_MESSAGE);
      } else {
        setSaveError(err instanceof Error ? err.message : 'Save failed');
      }
    } finally {
      setSaving(false);
      pendingRef.current = false;
      savingRef.current = false;
      if (savedOk) {
        notifyLocalSave();
      }
      if (retryable) {
        scheduleSaveRetry();
      } else {
        if (savedOk) {
          clearSaveRetry();
        }
        drainRemoteQueue();
      }
    }
  }

  useEffect(() => {
    if (!contentWasRepaired || repairPersistedRef.current) {
      return;
    }
    if (blocksContentEqual(safeInitialContent, initialContent)) {
      return;
    }
    repairPersistedRef.current = true;
    void flushSave(safeInitialContent);
  }, [contentWasRepaired, initialContent, safeInitialContent]);

  flushSaveRef.current = flushSave;

  function repairLinkArtifactsInEditor(): void {
    if (repairingLinksRef.current) {
      return;
    }
    repairingLinksRef.current = true;
    try {
      for (const block of editor.document as PartialBlock[]) {
        if (!block.id || !Array.isArray(block.content)) {
          continue;
        }
        const fixed = fixInlineLinkArtifacts(block.content);
        if (JSON.stringify(fixed) !== JSON.stringify(block.content)) {
          editor.updateBlock(block.id, { content: fixed });
        }
      }
    } finally {
      repairingLinksRef.current = false;
    }
  }

  function scheduleSave(): void {
    if (applyingRemoteRef.current) {
      return;
    }
    repairLinkArtifactsInEditor();
    console.log('[CS-IMG] onChange/scheduleSave', {
      inFlight: uploadsInFlightRef.current,
      imageBlocks: collectImageBlockSummary(editor.document as PartialBlock[]),
    });
    // While an upload is in flight, any onChange comes from BlockNote inserting
    // a placeholder block or applying intermediate updates. The post-upload
    // `setTimeout(0)` in `uploadFile`'s finally block is what triggers the real
    // save once the resolved url has been applied to the block. Clear any prior
    // debounce so a timer set before the upload started cannot fire mid-upload.
    if (uploadsInFlightRef.current > 0) {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
      return;
    }
    const blocks = validateBlocksForPersist(editor.document as PartialBlock[]);
    if (!blocks) {
      setSaveError('Unable to save: document contains invalid content');
      return;
    }
    setSaveError(null);
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      debounceRef.current = null;
      void flushSave();
    }, 500);
  }

  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!hasUnsavedChangesRef.current()) {
        return;
      }
      event.preventDefault();
      event.returnValue = '';
    };
    const onOnline = () => {
      if (!retryTimerRef.current) {
        return;
      }
      clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
      void flushSaveRef.current();
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    window.addEventListener('online', onOnline);
    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload);
      window.removeEventListener('online', onOnline);
    };
  }, []);

  useEffect(() => {
    const shell = editorShellRef.current;
    if (!shell) {
      return;
    }
    const onClick = (event: MouseEvent) => {
      const anchor = (event.target as HTMLElement | null)?.closest('a');
      if (!anchor) {
        return;
      }
      const href = anchor.getAttribute('href');
      if (!href || (!href.startsWith('/page/') && !href.startsWith('/database/'))) {
        return;
      }
      event.preventDefault();
      router.push(href);
    };
    shell.addEventListener('click', onClick);
    return () => {
      shell.removeEventListener('click', onClick);
    };
  }, [router]);

  return (
    <View className="mx-auto w-full max-w-content px-page pb-16 pt-2">
      {contentWasRepaired ? (
        <View className="mb-3 rounded-lg border border-warning bg-warning-muted px-3 py-2">
          <Text variant="body-sm" className="text-fg">
            Some content on this page was repaired after corruption. The fixed version is being
            saved.
          </Text>
        </View>
      ) : null}
      <div ref={editorShellRef} className="couplespace-editor min-h-[240px] w-full">
        <EditorPageProvider pageId={pageId}>
          <EditorLinkPickerProvider>
            <EditorDatabaseViewPickerProvider>
              <BlockNoteView
                editor={editor}
                onChange={scheduleSave}
                formattingToolbar={false}
                slashMenu={false}
                sideMenu={false}
                theme="light"
              >
                <CouplespaceSlashMenuController />
                <DatabaseViewPickerHost />
                <CouplespaceFormattingToolbarController />
              <SideMenuController
                sideMenu={(props) => (
                  <SideMenu {...props}>
                    <AddBlockButton />
                    <DragHandleButton dragHandleMenu={CouplespaceDragHandleMenu} />
                  </SideMenu>
                )}
              />
              </BlockNoteView>
            </EditorDatabaseViewPickerProvider>
          </EditorLinkPickerProvider>
        </EditorPageProvider>
      </div>
      <View className="mt-2 h-5">
        {saving ? <Text className="text-xs text-muted">Saving…</Text> : null}
        {saveError ? <Text variant="caption" className="text-danger">{saveError}</Text> : null}
      </View>
    </View>
  );
}
